import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import {
  applyTheme,
  DEFAULT_LAYOUT,
  DEFAULT_ACCENT_HEX,
  DEFAULT_BACKGROUND_HEX,
  DEFAULT_CARD_HEX,
  DEFAULT_FONT_SCALE,
  DEFAULT_READING_WIDTH,
  type LayoutDensity,
  type ThemePreferences,
} from '@/lib/theme';

interface ThemeContextValue {
  theme: ThemePreferences;
  loading: boolean;
  setTheme: (next: Partial<ThemePreferences>) => Promise<void>;
  resetTheme: () => Promise<void>;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const FALLBACK: ThemePreferences = {
  theme_background: DEFAULT_BACKGROUND_HEX,
  theme_accent: DEFAULT_ACCENT_HEX,
  theme_card: DEFAULT_CARD_HEX,
  theme_layout: DEFAULT_LAYOUT,
  theme_font_scale: DEFAULT_FONT_SCALE,
  theme_reading_width: DEFAULT_READING_WIDTH,
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [theme, setThemeState] = useState<ThemePreferences>(FALLBACK);
  const [loading, setLoading] = useState(true);

  // Apply the project default immediately so unauthenticated pages also use it.
  useEffect(() => {
    applyTheme(FALLBACK);
  }, []);

  useEffect(() => {
    if (!user?.id) {
      applyTheme(FALLBACK);
      setThemeState(FALLBACK);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    supabase
      .from('profiles')
      .select('theme_background, theme_accent, theme_card, theme_layout, theme_font_scale, theme_reading_width')
      .eq('id', user.id)
      .maybeSingle()
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          console.warn('Failed to load theme prefs:', error.message);
        }
        const merged: ThemePreferences = {
          theme_background: data?.theme_background ?? FALLBACK.theme_background,
          theme_accent: data?.theme_accent ?? FALLBACK.theme_accent,
          theme_card: (data as any)?.theme_card ?? FALLBACK.theme_card,
          theme_layout: (data?.theme_layout as LayoutDensity | null) ?? FALLBACK.theme_layout,
          theme_font_scale: (data as any)?.theme_font_scale ?? FALLBACK.theme_font_scale,
          theme_reading_width: (data as any)?.theme_reading_width ?? FALLBACK.theme_reading_width,
        };
        applyTheme(merged);
        setThemeState(merged);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const setTheme = useCallback(
    async (next: Partial<ThemePreferences>) => {
      const merged: ThemePreferences = { ...theme, ...next };
      // Apply optimistically so the UI flips immediately.
      applyTheme(merged);
      setThemeState(merged);
      if (!user?.id) return;
      const { error } = await supabase
        .from('profiles')
        .update({
          theme_background: merged.theme_background,
          theme_accent: merged.theme_accent,
          theme_card: merged.theme_card,
          theme_layout: merged.theme_layout,
          theme_font_scale: merged.theme_font_scale,
          theme_reading_width: merged.theme_reading_width,
        })
        .eq('id', user.id);
      if (error) {
        console.warn('Failed to persist theme prefs:', error.message);
      }
    },
    [theme, user?.id],
  );

  const resetTheme = useCallback(async () => {
    await setTheme(FALLBACK);
  }, [setTheme]);

  return (
    <ThemeContext.Provider value={{ theme, loading, setTheme, resetTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return ctx;
}
